import { yupResolver } from "@hookform/resolvers/yup"
import useAdminQueryParams from "hooks/useAdminQueryParams"
import { AntdButton, FormCheckbox, FormInputAntd, FormSelectAntd, GlobalSidebar, SidebarList } from "components"
import useAppDispatch from "hooks/useAppDispatch"
import useAppSelector from "hooks/useAppSelector"
import { useEffect, useMemo, useRef } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { SDEventTypesInitialValues, SDEventTypesSchema } from "Schema/StaticData/SDEventTypes"
import {
	getSDEventTypes,
	isSDEventTypesLoading,
	isSDEventTypesSaving,
	saveSDEventTypes,
	selectSDEventTypesById,
	selectSDEventTypesList,
} from "store/StaticData/SDEventTypes.slice"
import { SDEventTypesCountAsOptions, SDEventTypes as SDEventTypesInterface } from "types/staticData.types"
import { sortByProperty } from "config/utils"

interface SDEventTypeFormHandler extends Omit<SDEventTypesInterface, "_id"> {}

const SDEventTypes = () => {
	const dispatch = useAppDispatch()
	// const [selectedSDEventTypes, setSelectedSDEventTypes] = useState<string>("")
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSDEventTypes = "" } = params
	const saving = useAppSelector(isSDEventTypesSaving())
	const loading = useAppSelector(isSDEventTypesLoading())
	const SDEventTypesList = useAppSelector(selectSDEventTypesList)
	const SDEventTypesById = useAppSelector(selectSDEventTypesById(selectedSDEventTypes))
	const methods = useForm<SDEventTypeFormHandler>({ resolver: yupResolver(SDEventTypesSchema) })
	const isNewBeingCreated = useRef(false)

	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitting, isSubmitted },
	} = methods

	const sidebarSDEventTypes = useMemo(() => {
		return (SDEventTypesList ?? [])?.map(SDEventTypes => ({
			_id: SDEventTypes._id,
			name: SDEventTypes.name,
		}))
	}, [saving, SDEventTypesList?.length, isSubmitted])

	useEffect(() => {
		dispatch(getSDEventTypes())
	}, [])

	useEffect(() => {
		if (!selectedSDEventTypes && !!SDEventTypesList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDEventTypesList?.[0]?._id })
		}
	}, [params, SDEventTypesList])

	useEffect(() => {
		if (!selectedSDEventTypes && !isNewBeingCreated.current) reset(SDEventTypesInitialValues)
		else if (selectedSDEventTypes && SDEventTypesById) reset({ ...SDEventTypesInitialValues, ...SDEventTypesById })
	}, [selectedSDEventTypes, SDEventTypesById])

	const onSubmit: SubmitHandler<SDEventTypeFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(saveSDEventTypes(data, id => setSearchParams({ selected: id })))
	}

	const handleSelected = (SDEventTypeId: string): void => {
		setSearchParams({ selected: SDEventTypeId })
		isNewBeingCreated.current = false
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title="Event Types"
					loading={loading}
					selected={selectedSDEventTypes}
					setSelected={handleSelected}
					list={sidebarSDEventTypes.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>

			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-4 gap-y-4">
						<div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-2">
							<FormInputAntd type="text" label="Name" name="name" />
							<FormInputAntd type="text" label="Description" name="description" />

							<FormSelectAntd
								label="Counted As"
								name="countedAs"
								options={[
									{ label: "Select Counted As", value: "Select Counted As" },
									...SDEventTypesCountAsOptions,
								]}
							/>
						</div>
					</div>

					<div className="mt-2 grid grid-cols-4">
						<div className="col-span-2 grid grid-cols-2">
							<FormCheckbox label="Scheduled to Employee" name="scheduledToEmployee" />
							<FormCheckbox label="Scheduled to Post" name="scheduledToPost" />
							<FormCheckbox label="Results In Pay" name="resultsInPay" />
							<FormCheckbox label="Results In Bill" name="resultsInBill" />
							<FormCheckbox label="Is Enabled" name="isEnabled" />
						</div>
					</div>

					<div className="flex space-x-3 py-5">
						<AntdButton
							onClick={() => {
								isNewBeingCreated.current = true
								reset({ ...SDEventTypesInitialValues })
								setSearchParams({ selected: "" })
							}}
						>
							New
						</AntdButton>
						<AntdButton disabled={!isDirty || isSubmitting} loading={saving} htmlType="submit">
							{selectedSDEventTypes ? "Update" : "Save"}
						</AntdButton>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDEventTypes
